import * as React from "react";
import { Button, Input, theme } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { AutoSizer, List } from "react-virtualized";
import { Flex } from "src/components/Flex";
import { usePopupContext } from "src/hooks/usePopup";
import { createId } from "src/utils/strings";
import { fromKey } from "src/utils/fields";
import { Hover } from "src/components/Hover";
import { mono } from "src/fonts";
import type { ClauseMenuItem } from "./definitions";
import styles from "./question-input.module.css";

export type FilterSuggestions = {
  items: [string, (number | string)?][];
};

export function ClauseMenu(props: {
  items: ClauseMenuItem[];
  filterable?: boolean;
  onFilterChange?: (filter: string) => void;
  filterSuggestions?: FilterSuggestions[]; // TODO refactor, array not needed.
  valueParts?: { type: string; value: any | any[]; optional?: boolean }[];
  valuePartsLabel?: string;
  removeValueParts?: () => void;
  loadingMessage?: string;
}) {
  const { token } = theme.useToken();
  const [filter, setFilter] = React.useState("");
  const [inputId] = React.useState(createId());
  const suggestionCount = 0;
  let idx = props.filterable ? 1 + suggestionCount : 0;
  const items: (ClauseMenuItem & {
    index?: number;
    value: string;
  })[] = props.items.map((item) => ({
    ...item,
    value: item.key,
    index: item.disabled || item.aesthetic ? -1 : idx++,
  }));
  const indexedSuggestions = React.useMemo(
    () =>
      (props.filterSuggestions || []).reduce(
        (a, c) => [...a, ...c.items.map(([text]) => text)],
        [] as string[]
      ),
    [props.filterSuggestions]
  );
  const { filterable, onFilterChange } = props;
  const handleSelect = React.useCallback(
    (index: number) => {
      if (filterable && index <= suggestionCount) {
        if (index === 0)
          (
            document.querySelector(`#${inputId}`) as
              | HTMLInputElement
              | undefined
          )?.focus();
        else {
          const text = filter ? "" : indexedSuggestions[index - 1];
          setFilter(text);
          onFilterChange && onFilterChange(text);
        }
      } else {
        const item = items.find((item) => item.index === index);
        item?.onSelect();
        const typeFilters = (filter.match(/:[^:]+:/g) || []).join(" ").trim();
        setFilter(typeFilters);
        onFilterChange && onFilterChange(typeFilters);
      }
    },
    [
      items,
      inputId,
      suggestionCount,
      indexedSuggestions,
      filter,
      filterable,
      onFilterChange,
    ]
  );
  const [cursorIndex, setCursorIndex] = useKeyboardCursor(
    idx - 1,
    handleSelect,
    Boolean(props.filterable),
    suggestionCount
  );
  const [autoFocused, setAutoFocused] = React.useState(false);
  const { resize } = usePopupContext();
  const resizeTrigger = `${items.length}.${suggestionCount}.${filter}`;
  React.useEffect(() => {
    resize();
  }, [resizeTrigger, resize]);
  if (!filter && items.length === 0) {
    return (
      <Flex direction="column" gap={16} className={styles.loading}>
        <LoadingOutlined />
        <span>{props.loadingMessage || "loading options..."}</span>
      </Flex>
    );
  }
  return (
    <div style={{ display: "flex" }}>
      <div className={styles.menu}>
        {props.filterable && (
          <>
            <ClauseMenuItem
              key={"filter-input"}
              index={0}
              input
              cursorIndex={cursorIndex}
              setCursorIndex={setCursorIndex}
              onSelect={() => {}}
            >
              <Input
                id={inputId}
                autoFocus
                ref={(input) => {
                  setTimeout(() => {
                    if (input && !autoFocused) {
                      input.focus();
                      setAutoFocused(true);
                    }
                  }, 0);
                }}
                placeholder={"find..."}
                allowClear
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  props.onFilterChange && props.onFilterChange(e.target.value);
                }}
              />
            </ClauseMenuItem>
            {(props.filterSuggestions || [])
              .filter(({ items }) => items.length > 0)
              .map((group, gidx) => (
                <div key={gidx} className={styles.suggestions}>
                  {group.items.map(([text, count]) => (
                    <div key={`${gidx}--${text}`}>
                      <div
                        key={`${gidx}--${text}`}
                        className={`${styles.inline} ${
                          filter.includes(text) ? styles.hasClose : ""
                        }`}
                        onClick={() => {
                          const words = (filter || "").toLowerCase().split(" ");
                          if (words.includes(text.toLowerCase())) {
                            const newFilter = filter.replace(text, "").trim();
                            setFilter(newFilter);
                            props.onFilterChange &&
                              props.onFilterChange(newFilter);
                          } else {
                            const newFilter = `${
                              filter ? filter + " " : ""
                            }${text}`;
                            setFilter(newFilter);
                            props.onFilterChange &&
                              props.onFilterChange(newFilter);
                          }
                        }}
                      >
                        <span>{text}</span>
                        {filter.includes(text) ? (
                          <span className={styles.close}>
                            <CloseOutlined style={{ width: 12 }} />
                          </span>
                        ) : (
                          count !== undefined && (
                            <span className={styles.count}> {count}</span>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </>
        )}
        <div
          style={{
            maxHeight: "70vh",
            height: items.length > 50 ? "70vh" : "auto",
            overflow: items.length > 50 ? "hidden" : "auto",
          }}
        >
          {items.length > 50 ? (
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  width={width}
                  rowHeight={({ index }) => getRowHeight(items, index)}
                  rowCount={items.length}
                  rowRenderer={({ key, index, style }) => (
                    <div
                      key={key}
                      style={{
                        ...style,
                        height: getRowHeight(items, index),
                        overflow: "hidden",
                      }}
                    >
                      <ClauseMenuItem
                        key={items[index].key}
                        index={
                          items[index].index === undefined
                            ? -1
                            : items[index].index || 0
                        }
                        disabled={items[index].disabled}
                        aesthetic={items[index].aesthetic}
                        cursorIndex={cursorIndex}
                        setCursorIndex={setCursorIndex}
                        onSelect={handleSelect}
                      >
                        {items[index].isOption ? (
                          <div
                            className={
                              items[index].selectedOption
                                ? [styles.isOption, styles.selectedOption].join(
                                    " "
                                  )
                                : items[index].isPartial
                                ? [styles.isOption, styles.partialOption].join(
                                    " "
                                  )
                                : styles.isOption
                            }
                          >
                            <div className={styles.trunc}>
                              {items[index].label}
                              {items[index].labelKey ? (
                                <LabelKey
                                  color={items[index].metaColor}
                                  text={items[index].labelKey || ""}
                                />
                              ) : null}
                            </div>
                            {items[index].selectedOption && <CloseOutlined />}
                            {items[index].isPartial &&
                              !items[index].selectedOption && <CheckOutlined />}
                          </div>
                        ) : (
                          items[index].label
                        )}
                      </ClauseMenuItem>
                    </div>
                  )}
                />
              )}
            </AutoSizer>
          ) : (
            <>
              {items.map((item, index) => (
                <div
                  key={item.key}
                  style={{
                    height: getRowHeight(items, index),
                    overflow: "hidden",
                  }}
                >
                  <ClauseMenuItem
                    index={item.index === undefined ? -1 : item.index}
                    disabled={item.disabled}
                    aesthetic={item.aesthetic}
                    cursorIndex={cursorIndex}
                    setCursorIndex={setCursorIndex}
                    onSelect={handleSelect}
                  >
                    {item.isOption ? (
                      <div
                        className={
                          item.selectedOption
                            ? [styles.isOption, styles.selectedOption].join(" ")
                            : item.isPartial
                            ? [styles.isOption, styles.partialOption].join(" ")
                            : styles.isOption
                        }
                      >
                        <div className={styles.trunc}>
                          {item.label}
                          {item.labelKey ? (
                            <LabelKey
                              color={item.metaColor}
                              text={item.labelKey}
                            />
                          ) : null}
                        </div>
                        {item.selectedOption && <CloseOutlined />}
                        {item.isPartial && !item.selectedOption && (
                          <CheckOutlined />
                        )}
                      </div>
                    ) : (
                      item.label
                    )}
                  </ClauseMenuItem>
                </div>
              ))}
            </>
          )}
          {items.every((item) => item.selectedOption) && (
            <div className={styles.empty}>no items found</div>
          )}
        </div>
      </div>
      {((props.valuePartsLabel && props.removeValueParts) ||
        (props.valueParts && props.valueParts.length > 0)) && (
        <div
          className={styles.valueParts}
          style={{
            flexDirection: "column",
            display: "flex",
            padding: "6px 12px 10px",
            width: 250,
            overflow: "auto",
            backgroundColor: token.colorBgLayout,
            borderRadius: "0 10px 10px 0",
            gap: 4,
            ...mono.style,
          }}
        >
          <Flex
            spaceBetween
            style={{
              padding: "9px 4px",
              borderBottom: `solid 1px ${token.colorBgBase}`,
              marginBottom: 8,
            }}
          >
            <span>{props.valuePartsLabel}</span>
            <Button
              onClick={() => props.removeValueParts && props.removeValueParts()}
              type="text"
              shape="circle"
              size="small"
            >
              <DeleteOutlined />
            </Button>
          </Flex>
          {(props.valueParts || []).map((part) => (
            <Hover
              key={JSON.stringify(part)}
              baseStyle={{
                fontSize: 12,
                cursor: "pointer",
                backgroundColor: (filter || "").split(" ").includes(part.type)
                  ? token.colorHighlight
                  : token.colorBgBase,
                color: (filter || "").split(" ").includes(part.type)
                  ? token.colorText
                  : token.colorTextSecondary,
                width: "100%%",
                overflow: "hidden",
                padding: "8px 8px",
                alignItems: "flex-end",
                gap: 6,
                borderRadius: 5,
              }}
              hoverStyle={{
                color: token.colorTextLightSolid,
                backgroundColor: token.colorHighlight,
              }}
            >
              <div
                key={JSON.stringify(part)}
                onClick={() => {
                  let minFilter = filter || ""; //.toLowerCase();
                  const removing = minFilter.includes(part.type);
                  props.valueParts?.forEach(({ type }) => {
                    minFilter = minFilter.replace(type, "");
                  });
                  const newFilter = removing ? minFilter : part.type;
                  setFilter(newFilter);
                  props.onFilterChange && props.onFilterChange(newFilter);
                }}
              >
                <div>{part.type.slice(1, -1)}</div>
                <div
                  style={{
                    fontSize: 14,
                    color:
                      !part.value || part.value.length === 0
                        ? part.optional
                          ? token.colorInfoText
                          : token.colorErrorText
                        : token.colorText,
                  }}
                  className="trunc"
                >
                  {(Array.isArray(part.value)
                    ? (part.value.at(0) === "__VZBL::null__"
                        ? "n/a"
                        : part.value.at(0)) +
                      (part.value.length > 1
                        ? `, +${part.value.length - 1}`
                        : "")
                    : part.value) || (part.optional ? "optional" : "required")}
                </div>
              </div>
            </Hover>
          ))}
        </div>
      )}
    </div>
  );
}

function LabelKey({ color, text }: { color?: string; text: string }) {
  const field = fromKey(text);
  return (
    <div className={styles.labelKey}>
      {color && (
        <div
          style={{
            backgroundColor: color,
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 3,
            marginRight: 3,
          }}
        />
      )}
      <span>{field.target}-&gt; </span>
      <span>
        {field.attribute === "__VZBL::null__" ? "n/a" : field.attribute}
      </span>
    </div>
  );
}

function ClauseMenuItem(
  props: React.PropsWithChildren<{
    index: number;
    disabled?: boolean;
    aesthetic?: boolean;
    input?: boolean;
    inline?: boolean;
    cursorIndex: number;
    setCursorIndex(cursorIndex: number): void;
    onSelect(index: number): void;
  }>
) {
  const focused = props.index === props.cursorIndex;
  const ref = useFocusRef(focused);
  const classNames = [styles.menuItem];
  if (focused) classNames.push(styles.active);
  if (props.input) classNames.push(styles.headerInput);
  if (props.aesthetic) classNames.push(styles.aesthetic);
  if (props.disabled) classNames.push(styles.disabled);
  if (props.inline) classNames.push(styles.inline);
  return (
    <div
      ref={ref}
      className={classNames.join(" ")}
      onMouseMove={() => props.index >= 0 && props.setCursorIndex(props.index)}
      onClick={() => props.index >= 0 && props.onSelect(props.index)}
    >
      {props.children}
    </div>
  );
}

function useKeyboardCursor(
  maxIndex: number,
  onSelect: (index: number) => void,
  filterable: boolean,
  filterCutoff: number
) {
  const [cursorIndex, setCursorIndex] = React.useState(-1);
  const effectRef = React.useRef({
    cursorIndex,
    setCursorIndex,
    maxIndex,
    onSelect,
    filterable,
    filterCutoff,
  });
  effectRef.current = {
    cursorIndex,
    setCursorIndex,
    maxIndex,
    onSelect,
    filterable,
    filterCutoff,
  };
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Enter": {
          event.preventDefault();
          effectRef.current.onSelect(effectRef.current.cursorIndex);
          break;
        }
        case "ArrowDown": {
          event.preventDefault();
          effectRef.current.setCursorIndex((prev) => {
            if (
              effectRef.current.filterCutoff > 0 &&
              prev < effectRef.current.filterCutoff &&
              prev > 0
            )
              return effectRef.current.filterCutoff + 1;
            return prev < effectRef.current.maxIndex ? prev + 1 : prev; // change to 0 for wrap-around
          });
          break;
        }
        case "ArrowUp": {
          let shouldPrevent = false;
          effectRef.current.setCursorIndex((prev) => {
            if (
              effectRef.current.filterCutoff > 0 &&
              prev <= effectRef.current.filterCutoff &&
              prev > 0
            )
              return 0;
            if (
              effectRef.current.filterCutoff > 0 &&
              prev === effectRef.current.filterCutoff + 1
            )
              return 1;
            shouldPrevent = prev > 0;
            return prev > 0 ? prev - 1 : prev; // change to effectRef.current.maxIndex for wrap-aroudn
          });
          shouldPrevent && event.preventDefault();
          break;
        }
        case "ArrowLeft": {
          let shouldPrevent = false;
          effectRef.current.setCursorIndex((prev) => {
            if (prev === 1) return effectRef.current.filterCutoff;
            if (prev > 0 && prev <= effectRef.current.filterCutoff) {
              shouldPrevent = true;
              return prev - 1;
            }
            return prev;
          });
          shouldPrevent && event.preventDefault();
          break;
        }
        case "ArrowRight": {
          if (effectRef.current.filterCutoff === 0) return;
          let shouldPrevent = false;
          effectRef.current.setCursorIndex((prev) => {
            if (prev === effectRef.current.filterCutoff) return 1;
            if (prev > 0 && prev <= effectRef.current.filterCutoff) {
              shouldPrevent = true;
              return prev + 1;
            }
            return prev;
          });
          shouldPrevent && event.preventDefault();
          break;
        }
        case "/": {
          let shouldPrevent = false;
          effectRef.current.setCursorIndex((prev) => {
            if (prev !== 0) shouldPrevent = true;
            return 0;
          });
          shouldPrevent && event.preventDefault();
          break;
        }
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, []);
  React.useEffect(() => setCursorIndex(0), [maxIndex]);
  return [cursorIndex, setCursorIndex] as const;
}

function useFocusRef(focused: boolean) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (ref.current) {
      ref.current.tabIndex = -1;
      if (focused) {
        ref.current.focus();
      }
    }
  }, [focused]);
  return ref;
}

export function useMenuEscape() {
  const { close: closePopup } = usePopupContext();
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePopup();
      }
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [closePopup]);
}

function getRowHeight(items: ClauseMenuItem[], index: number) {
  return items[index].labelKey
    ? items[index].labelKey == "Value.numbers"
      ? 64
      : items[index].labelKey == "Value.datetimes"
      ? 80
      : 48
    : 34;
}
